import gsap from 'gsap'
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import useIntersectionObserver from 'components/core/Intersection/hooks/useIntersectionObserver'

const DEFAULT_SCOPE = ':scope > *'
const DEFAULT_CONFIG = {
  delayShow: 0.15,
  durationShow: 0.75,
  durationHide: 0.25,
  y: 5
}

/**
 * The `useActiveAnimationChildren` handles the transitioning in
 * (reveal) of child elements of a given react rect
 *
 * @param {object} ref - the ref
 * @param {boolean} isActive - whether is active
 * @param {string} selector - the selector to target
 * @param {object} config - the input config
 */
const useActiveStateAnimationChildren = (
  ref,
  isActive = true,
  selector = DEFAULT_SCOPE,
  config = DEFAULT_CONFIG
) => {
  const wasActive = useRef(false)
  const gEl = gsap.utils.selector(ref)
  const tl = useRef()
  const isIntersecting = useIntersectionObserver(ref)
  const mountTime = useMemo(() => Date.now(), [isActive])

  /**
   * Effect: On unmount
   */
  useEffect(
    () => () => {
      if (tl.current) {
        gsap.killTweensOf(tl.current)
      }
    },
    []
  )

  /**
   * Layout Effect: When the selector changes,
   * create the timeline
   */
  useLayoutEffect(() => {
    wasActive.current = false

    const elements = gEl(selector)

    if (!elements.length) {
      return
    }

    tl.current = gsap
      .timeline()
      .from(elements, {
        y: config.y,
        opacity: 0
      })
      .to(elements, {
        y: 0,
        opacity: 1,
        stagger: {
          each: 0.05,
          from: 'start'
        }
      })

    tl.current.pause()
  }, [selector])

  /**
   * Effect: When the `isActive` or `isIntersecting` state changes
   */
  useEffect(() => {
    if (!isIntersecting || wasActive.current === isActive) {
      return
    }

    wasActive.current = isActive

    if (!tl.current) {
      // Defensive
      return
    }

    gsap.killTweensOf(tl.current)

    if (isActive) {
      // Is active
      const now = Date.now()
      const diff = now - mountTime

      gsap.set(tl.current, { progress: 0 })

      gsap.to(tl.current, {
        progress: 1,
        duration: config.durationShow,
        delay: diff > 500 ? 0.2 : config.delayShow,
        ease: 'sine.out'
      })

      return
    }

    // Is not active
    gsap.to(tl.current, {
      progress: 0,
      duration: config.durationHide,
      ease: 'sine.in'
    })
  }, [isActive, isIntersecting])
}

// Export
export default useActiveStateAnimationChildren
